import React, { useRef, useState } from "react";
import {
  ScanLine, X, Camera,
  Image as ImageIcon, Loader2, Plus,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * MobileQuickActions
 * ──────────────────────────────────────────────────────────────────
 * Floating action button (FAB) shown ONLY on small screens (`lg:hidden`).
 * Lets the consultant trigger quick document scanning actions on a client
 * without scrolling through the desktop UI.
 *
 * Actions:
 *   • Scanner Dokument — opens the phone camera (or photo gallery) and
 *     uploads the resulting image to `POST /documents/upload`.
 */
export default function MobileQuickActions({ client, onAfterUpload }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  if (!client?.id) return null;

  const close = () => setOpen(false);

  const handleFile = async (file, source) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("client_id", client.id);
      fd.append("category", "insurance");
      fd.append("doc_type", "scan_mobile");
      await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        source === "camera"
          ? "Dokument vom Kamera hochgeladen — AI analysiert es jetzt"
          : "Dokument hochgeladen — AI analysiert es jetzt"
      );
      onAfterUpload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Upload");
    } finally {
      setBusy(false);
      close();
    }
  };

  return (
    <>
      {/* Hidden file inputs that drive the OS pickers */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f, "camera");
          e.target.value = "";
        }}
        data-testid="mqa-camera-input"
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f, "gallery");
          e.target.value = "";
        }}
        data-testid="mqa-gallery-input"
      />

      {/* Floating action button — only visible below `lg` breakpoint */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="lg:hidden fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-prime text-graphite shadow-xl shadow-prime/40 flex items-center justify-center active:scale-95 transition-transform"
        aria-label="Schnellaktionen"
        data-testid="mqa-fab"
      >
        <Plus className="w-6 h-6" strokeWidth={2.5} />
      </button>

      {/* Bottom sheet menu */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mqa-sheet">
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Schliessen"
            onClick={close}
            className="absolute inset-0 bg-black/60"
          />
          {/* Sheet */}
          <div className="absolute bottom-0 left-0 right-0 bg-card rounded-t-2xl shadow-2xl pb-[env(safe-area-inset-bottom)]">
            <div className="flex items-center justify-between px-5 pt-4 pb-2 border-b border-border">
              <div>
                <div className="label-uppercase text-prime">Schnellaktion</div>
                <div className="text-sm font-semibold truncate max-w-[240px]">
                  {client.first_name} {client.last_name}
                </div>
              </div>
              <button
                type="button"
                onClick={close}
                className="p-2 rounded-md hover:bg-muted text-muted-foreground"
                aria-label="Schliessen"
                data-testid="mqa-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {busy && (
              <div className="px-5 py-3 flex items-center gap-2 text-xs text-muted-foreground border-b border-border">
                <Loader2 className="w-4 h-4 animate-spin" /> bitte warten…
              </div>
            )}

            <div className="p-3 grid grid-cols-2 gap-2">
              {/* Scanner via camera */}
              <ActionTile
                icon={<Camera className="w-6 h-6" strokeWidth={1.6} />}
                label="Dokument scannen"
                hint="Kamera öffnen"
                disabled={busy}
                onClick={() => cameraRef.current?.click()}
                testId="mqa-action-scan-camera"
              />
              {/* Scanner via gallery */}
              <ActionTile
                icon={<ImageIcon className="w-6 h-6" strokeWidth={1.6} />}
                label="Aus Galerie"
                hint="Foto/PDF wählen"
                disabled={busy}
                onClick={() => galleryRef.current?.click()}
                testId="mqa-action-scan-gallery"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ActionTile({ icon, label, hint, onClick, disabled, testId, colSpan = 1 }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`${colSpan === 2 ? "col-span-2" : ""} flex flex-col items-center justify-center gap-1 p-4 rounded-lg border border-border bg-background hover:bg-prime/5 hover:border-prime/40 active:scale-[0.98] transition disabled:opacity-50 disabled:pointer-events-none`}
    >
      <span className="text-prime">{icon}</span>
      <span className="text-sm font-semibold text-foreground text-center">{label}</span>
      {hint && (
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground text-center truncate max-w-[120px]">
          {hint}
        </span>
      )}
    </button>
  );
}

// Re-export icons that consumers may want to render alongside (for future use).
export { ScanLine };
